/**
 * 箭头线图层 — BMapGL.LineLayer + 纹理箭头封装
 *
 * 层级：map-core（"地图怎么动"）
 *
 * 职责：
 * 1. 创建带纹理箭头的 LineLayer（strokeTextureUrl 沿线方向重复铺贴）
 * 2. 返回最小 handle：setData 更新线串 / dispose 下图
 *
 * 坑 18 铁律（FeatureLayer 系）：
 * - 全局 coordType 不传递到 NormalLayer，必须显式 crs: 'GCJ02'
 * - NormalLayer 默认 maxZoom 21，地图 maxZoom 25，必须显式 maxZoom: 25
 *
 * 不知道业务存在，不知道 React 存在
 */

export interface ArrowLineOptions {
  /** 线色（纹理打底色，GCJ02 坐标下渲染） */
  color: string;
  /** 线宽 */
  weight: number;
  /** 纹理 dataURL（竖向条纹，箭头方向沿线） */
  textureUrl: string;
  /** 纹理铺贴尺寸（与纹理图实际尺寸一致，缺省 SDK 按默认缩放会糊） */
  textureWidth: number;
  textureHeight: number;
}

export interface ArrowLineHandle {
  setData(coords: [number, number][]): void;
  dispose(): void;
}

const toLineGeoJSON = (coords: [number, number][]) => ({
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: coords },
    },
  ],
});

/**
 * 创建箭头线图层并上图（先例：drawRectangle.ts addFeatureLayer —
 * addNormalLayer + setData({changeCenter: true}) 以首坐标设渲染参考中心防抖动）
 */
export const createArrowLine = (
  bdMap: BMapGL.Map,
  coords: [number, number][],
  options: ArrowLineOptions
): ArrowLineHandle => {
  const layer = new window.BMapGL.LineLayer({
    // 显式声明数据源为 GCJ02（全局 coordType 不传递到 FeatureLayer 系图层）
    crs: 'GCJ02',
    // 对齐地图 maxZoom=25，避免超过默认 21 被自动隐藏
    maxZoom: 25,
    // 纹理参数全量对齐 LogisticMap useLines.ts buildBaseStyle（隔壁已验证的正常效果）：
    // sequence:false 防止 SDK 把纹理当帧动画；显式纹理尺寸保证按原尺寸铺贴
    style: {
      sequence: false,
      borderColor: 'rgba(0,0,0,0.15)',
      borderMask: true,
      borderWeight: 0,
      strokeLineJoin: 'round',
      strokeLineCap: 'round',
      strokeColor: options.color,
      strokeWeight: options.weight,
      strokeTextureUrl: options.textureUrl,
      strokeTextureWidth: options.textureWidth,
      strokeTextureHeight: options.textureHeight,
    },
  });
  bdMap.addNormalLayer?.(layer);
  // changeCenter：以首个要素首坐标作为渲染参考中心（大比例尺防抖动）
  layer.setData(toLineGeoJSON(coords), { changeCenter: true });

  return {
    setData: (nextCoords: [number, number][]) => {
      layer.setData(toLineGeoJSON(nextCoords), { changeCenter: true });
    },
    dispose: () => {
      bdMap.removeNormalLayer?.(layer);
    },
  };
};
